import React from "react";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import PrintIcon from "@mui/icons-material/Print";
import { showToastError } from "common/toast/toast";
import { generatePropertyShareLink } from "../../api/api";
import { MUILoadingButton } from "../../components/buttons/MUILoadingButton";
import { getExternalLink } from "../../components/links/ExternalLink";
import { useProperty } from "../../hooks/property.hooks";
import { useOrganization } from "../../hooks/organization.hooks";
import { PropertyPermission } from "./PropertyShareSettings";
import { usePenCertificate } from "./hooks/usePenCertificate";

interface PropertyLocationPdfButtonProps {
  propertyId: string;
}

export const PropertyLocationPdfButton: React.FC<
  PropertyLocationPdfButtonProps
> = ({ propertyId }) => {
  const { t } = useTranslation();
  const { data: property } = useProperty(propertyId);
  const { data: organization } = useOrganization();
  const { hasPenCertificate } = usePenCertificate(property?.uniqueNr);

  const { isLoading, mutate } = useMutation(generatePropertyShareLink, {
    onSuccess: (data) => {
      if (!data.pdfUrl) {
        showToastError();
        return;
      }
      window.location.href = getExternalLink(data.pdfUrl);
    },
    onError: () => {
      showToastError();
    },
  });

  const locationPermissions = [
    PropertyPermission.ViewDistances,
    PropertyPermission.ViewHazardousObjects,
    PropertyPermission.ViewMapsAndClimateRisks,
    ...(hasPenCertificate ? [PropertyPermission.ViewPenCertificate] : []),
  ];

  return (
    <MUILoadingButton
      variant="outlined"
      loading={isLoading}
      startIcon={<PrintIcon />}
      onClick={() =>
        mutate({
          propertyId,
          permissions: locationPermissions,
          pdf: true,
          deleteFilesAfterReportGeneration:
            organization?.deleteFilesAfterReportGeneration ?? false,
        })
      }
    >
      {t("propertyShare.printLocationReport")}
    </MUILoadingButton>
  );
};
